import { requestWithAuthorization } from "../network/apiRequests";

// Interface matching the actual API response structure
export interface ApiLicenseResponse {
  current_usage: number;
  plan_id: string;
  remaining_requests: number;
  requests_per_month: number;
  role: string;
  user_id: string;
}

// Interface used by UI components (kept for backward compatibility)
export interface LicenseInfo {
  role: string;
  plan: string;
  usage: number;
  limits: number;
  remaining: number;
  license_created_at: string;
  license_updated_at: string;
}

export class LicenseService {
  private static readonly BASE_URL = process.env.REACT_APP_API_HOSTNAME || "";

  static async getUserLicenseInfo(): Promise<LicenseInfo> {
    try {
      const response = await requestWithAuthorization(
        `${this.BASE_URL}/user/license`
      );

      if (response.status === 200) {
        const apiData: ApiLicenseResponse = response.data;
        // Map API response to UI interface
        return this.mapApiToLicenseInfo(apiData);
      }
      throw new Error(`Failed to fetch license info: ${response.status}`);
    } catch (error) {
      console.error("Error fetching license info:", error);
      throw error;
    }
  }

  private static mapApiToLicenseInfo(apiData: ApiLicenseResponse): LicenseInfo {
    return {
      role: apiData.role,
      plan: apiData.plan_id,
      usage: apiData.current_usage,
      limits: apiData.requests_per_month,
      remaining: apiData.remaining_requests,
      license_created_at: '', // API doesn't provide this
      license_updated_at: '', // API doesn't provide this
    };
  }

  static formatLicensePlan(plan: string): string {
    // Convert plan names to more readable format
    switch (plan?.toLowerCase()) {
      case 'free':
        return 'Free Plan';
      case 'pro':
        return 'Pro Plan';
      case 'admin':
        return 'Administrator Plan';
      default:
        return plan || 'Unknown Plan';
    }
  }

  static formatRole(role: string): string {
    // Convert role names to more readable format
    switch (role?.toLowerCase()) {
      case 'user':
        return 'User';
      case 'admin':
        return 'Administrator';
      case 'moderator':
        return 'Moderator';
      default:
        return role || 'User';
    }
  }

  static formatDate(dateString: string): string {
    if (!dateString) return 'N/A';

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return 'Invalid Date';
    }
  }

  static getUsagePercentage(usage: number, limits: number): number {
    if (limits === -1 || limits === 0) return 0; // Unlimited or no limits
    return Math.min((usage / limits) * 100, 100);
  }

  static getUsageStatus(usage: number, limits: number): { status: string; color: string } {
    if (limits === -1) {
      return { status: 'Unlimited', color: '#067e93' }; // Blue for unlimited
    }

    const percentage = this.getUsagePercentage(usage, limits);

    if (percentage >= 90) {
      return { status: 'Critical', color: '#dc3545' }; // Red
    } else if (percentage >= 70) {
      return { status: 'Warning', color: '#ffc107' }; // Yellow
    } else {
      return { status: 'Healthy', color: '#28a745' }; // Green
    }
  }

  static formatUsageDisplay(usage: number, limits: number): string {
    if (limits === -1) {
      return `${usage.toLocaleString()} requests (Unlimited)`;
    }
    return `${usage.toLocaleString()} / ${limits.toLocaleString()} requests`;
  }

  static isUnlimitedPlan(limits: number): boolean {
    return limits === -1;
  }
}